import type { Address, FeatureFlags, NavItem, OrderStatus, PaymentMethod } from '@/types';
import type {
  AdminBanner, AdminCampaign, AdminCategory, AdminCoupon, AdminCustomer, AdminOffer, AdminOrder, AdminProduct, BrandingSettings,
  Commission, InventoryEntry, LoyaltyConfig, LoyaltyEntry, LoyaltyReward, NavigationSettings, ProductVariant, ReferralLink,
  ReferralSettings, ReferredCustomer, StoreSettings, TeamMember, ThemeSettings, WalletEntry,
} from '@/types/admin';
import type { TenantStatus } from '@/types/platform';
import { computeEffectiveFeatures, type FeatureOverrides } from '@/lib/featureRegistry';
import { PLAN_DEFS, planById } from './plans';
import { categoriesSeed, productsSeed } from './catalog';
import { banners as bannerSeed } from './marketing';
import { couponsSeed } from './account';
import { demoTenant } from './tenant';

/** "Today" for seeded data. Analytics anchor on the newest order so the demo never looks empty. */
export const SEED_NOW = Date.parse('2026-09-19T12:00:00Z');
const DAY = 86_400_000;
const iso = (t: number) => new Date(t).toISOString();

/** Deterministic PRNG so the seeded demo is identical on every load. */
function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface TenantData {
  tenantId: string;
  name: string;
  slug: string;
  /** Platform-assigned. Combined with `planId`'s default flags via `computeEffectiveFeatures` to CACHE the
      result here. This cache is what every tenant-scoped read (ctx, storefront) uses; it is recomputed by
      `syncTenantFeatures` (services/mock/platform) whenever the plan or overrides change — see PROJECT_CONTEXT.md
      "Plan Feature + Tenant Override = Effective Feature". */
  features: FeatureFlags;
  status: TenantStatus;
  createdAt: string;
  ownerName: string;
  ownerEmail: string;
  planId: string;
  featureOverrides: FeatureOverrides;
  products: AdminProduct[];
  categories: AdminCategory[];
  inventory: InventoryEntry[];
  orders: AdminOrder[];
  customers: AdminCustomer[];
  addresses: Record<string, Address[]>;
  wallet: WalletEntry[];
  loyalty: LoyaltyEntry[];
  coupons: AdminCoupon[];
  banners: AdminBanner[];
  offers: AdminOffer[];
  campaigns: AdminCampaign[];
  loyaltyConfig: LoyaltyConfig;
  rewards: LoyaltyReward[];
  referralSettings: ReferralSettings;
  referralLinks: ReferralLink[];
  referred: ReferredCustomer[];
  commissions: Commission[];
  store: StoreSettings;
  branding: BrandingSettings;
  theme: ThemeSettings;
  navigation: NavigationSettings;
  system: { notifications: { newOrder: boolean; lowStock: boolean; newCustomer: boolean; payoutRequest: boolean; dailyDigest: boolean }; orderPrefix: string; team: TeamMember[] };
  traffic: { date: string; visits: number; carts: number; checkouts: number }[];
  idempotency: Record<string, string>;
  notificationsRead: string[];
}

const NAMES = [
  'Aarav Sharma', 'Priya Nair', 'Rahul Verma', 'Sneha Iyer', 'Mohammed Farhan', 'Kavya Reddy', 'Arjun Mehta', 'Divya Menon',
  'Vikram Singh', 'Ananya Das', 'Rohit Kulkarni', 'Neha Kapoor', 'Siddharth Rao', 'Isha Gupta', 'Karan Malhotra', 'Pooja Bhat',
  'Aditya Joshi', 'Meera Pillai', 'Harsh Vora', 'Ritu Sen', 'Nikhil Chawla', 'Tanvi Desai', 'Yash Agarwal', 'Simran Kaur',
  'Dev Patel', 'Lakshmi Krishnan', 'Manish Tiwari', 'Sara Khan', 'Gautam Bose', 'Aisha Siddiqui', 'Varun Nambiar', 'Ira Banerjee',
  'Tarun Saxena', 'Diya Shetty', 'Om Prakash', 'Zoya Ansari',
];
const CITIES: [string, string, string][] = [
  ['Bengaluru', 'Karnataka', '5600'], ['Mumbai', 'Maharashtra', '4000'], ['Delhi', 'Delhi', '1100'], ['Hyderabad', 'Telangana', '5000'],
  ['Chennai', 'Tamil Nadu', '6000'], ['Pune', 'Maharashtra', '4110'], ['Kolkata', 'West Bengal', '7000'], ['Ahmedabad', 'Gujarat', '3800'],
];

const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export interface SeedOptions {
  tenantId: string;
  name: string;
  slug: string;
  seed: number;
  productCount: number;
  customerCount: number;
  orderCount: number;
  ownerEmail: string;
  ownerName?: string;
  prefix: string;
  /** Plan id from `data/plans.ts` (or a live platform plan with the same shape). Required. */
  planId: string;
  featureOverrides?: FeatureOverrides;
  status?: TenantStatus;
  createdAt?: string;
}

export function seedTenant(o: SeedOptions): TenantData {
  const r = rng(o.seed);
  const pick = <T,>(a: T[]) => a[Math.floor(r() * a.length)];
  const T = o.tenantId;
  const idp = (p: string, i: number) => `${p}_${o.slug}_${String(i).padStart(3, '0')}`;

  /* ---------- categories & products ---------- */
  const catIds = new Set(productsSeed.slice(0, o.productCount).map((p) => p.categoryId));
  const categories: AdminCategory[] = categoriesSeed
    .filter((c) => catIds.has(c.id))
    .map((c, i) => ({ ...c, id: `${c.id}_${o.slug}`, tenantId: T, status: 'ACTIVE' as const, sortOrder: i, productCount: 0 }));
  const catId = (orig: string) => `${orig}_${o.slug}`;

  const products: AdminProduct[] = productsSeed.slice(0, o.productCount).map((p, i) => {
    const status = i === 21 ? 'DRAFT' : i === 22 ? 'DRAFT' : i === 23 ? 'ARCHIVED' : 'ACTIVE';
    // a few realistic stock problems for dashboards
    const stock = i === 5 ? 8 : i === 12 ? 5 : i === 13 ? 0 : i === 17 ? 14 : i === 9 ? 0 : p.stock;
    const o1 = p.options[0];
    const o2 = p.options[1];
    const combos: Record<string, string>[] = [];
    if (o1) for (const v1 of o1.values) { if (o2) for (const v2 of o2.values) combos.push({ [o1.name]: v1, [o2.name]: v2 }); else combos.push({ [o1.name]: v1 }); }
    const vlist = combos.slice(0, 6);
    const variants: ProductVariant[] = vlist.map((opts, vi) => ({
      id: `${idp('var', i + 1)}_${vi}`, options: opts, sku: `${p.sku}-${vi + 1}`, price: p.price,
      stock: Math.floor(stock / Math.max(vlist.length, 1)) + (vi === 0 ? stock % Math.max(vlist.length, 1) : 0),
    }));
    return {
      ...p, id: idp('prod', i + 1), categoryId: catId(p.categoryId), tenantId: T, status, stock, variants,
      costPrice: Math.round(p.price * 0.58), taxable: true, trackInventory: true,
      seoTitle: `${p.name} | ${o.name}`, seoDescription: p.description.split('\n')[0].slice(0, 155),
      updatedAt: iso(SEED_NOW - (i % 9) * DAY * 2), createdAt: iso(SEED_NOW - (120 + i * 3) * DAY),
    };
  });
  const active = products.filter((p) => p.status === 'ACTIVE');

  /* ---------- customers ---------- */
  const customers: AdminCustomer[] = NAMES.slice(0, o.customerCount).map((name, i) => {
    const [city] = CITIES[i % CITIES.length];
    return {
      id: idp('cus', i + 1), tenantId: T, name, email: `${name.toLowerCase().replace(/[^a-z]+/g, '.')}@example.com`,
      phone: `+91 9${String(800000000 + Math.floor(r() * 199999999)).slice(0, 9)}`,
      status: i === 27 ? 'BLOCKED' : 'ACTIVE', joinedAt: iso(SEED_NOW - (15 + Math.floor(r() * 170)) * DAY),
      lastActiveAt: iso(SEED_NOW - 1000), ordersCount: 0, totalSpent: 0, walletBalance: 0, loyaltyPoints: 0, tags: [], city,
    };
  });
  const addresses: Record<string, Address[]> = {};
  customers.forEach((c, i) => {
    const [city, state, pin] = CITIES[i % CITIES.length];
    addresses[c.id] = [{
      id: `addr_${c.id}`, label: 'Home', name: c.name, phone: c.phone!, line1: `${10 + i}, ${pick(['MG Road', 'Park Street', 'Lake View', 'Green Avenue', 'Church Lane'])}`,
      city, state, pincode: `${pin}${String(10 + (i % 80)).padStart(2, '0')}`, isDefault: true,
    }];
  });

  /* ---------- orders + wallet/loyalty ledgers (single chronological pass) ---------- */
  const wallet: WalletEntry[] = [];
  const loyalty: LoyaltyEntry[] = [];
  const spendable: Record<string, number> = {};
  let wn = 1;
  let ln = 1;
  const addWallet = (c: AdminCustomer, type: WalletEntry['type'], direction: 'CREDIT' | 'DEBIT', amount: number, at: number, extra: Partial<WalletEntry> = {}) =>
    wallet.push({ id: idp('wtx', wn++), tenantId: T, customerId: c.id, customerName: c.name, type, direction, amount, balanceAfter: 0, createdAt: iso(at), ...extra });
  const addLoyalty = (c: AdminCustomer, type: LoyaltyEntry['type'], points: number, description: string, at: number) =>
    loyalty.push({ id: idp('ltx', ln++), tenantId: T, customerId: c.id, customerName: c.name, type, points, description, createdAt: iso(at) });

  type Ev = { t: number; kind: 'topup'; c: AdminCustomer; amount: number } | { t: number; kind: 'order'; idx: number };
  const events: Ev[] = [];
  customers.forEach((c) => {
    addLoyalty(c, 'BONUS', 20, 'Sign up bonus', Date.parse(c.joinedAt));
    if (r() < 0.62) {
      const n = 1 + Math.floor(r() * 3);
      for (let k = 0; k < n; k++) {
        const t = Date.parse(c.joinedAt) + (1 + Math.floor(r() * 40)) * DAY;
        if (t < SEED_NOW) events.push({ t, kind: 'topup', c, amount: pick([500, 1000, 1500, 2000, 3000]) });
      }
      // a recent top-up for many wallet users so the last-30-days activity chart is meaningful
      const recent = SEED_NOW - Math.floor(r() * 29) * DAY - Math.floor(r() * DAY * 0.8);
      if (r() < 0.55 && recent > Date.parse(c.joinedAt)) events.push({ t: recent, kind: 'topup', c, amount: pick([500, 1000, 1500, 2000]) });
    }
  });

  const orders: AdminOrder[] = [];
  for (let i = 0; i < o.orderCount; i++) {
    const ageDays = Math.pow(r(), 1.15) * 90;
    const placed = SEED_NOW - ageDays * DAY - Math.floor(r() * DAY * 0.6);
    const eligible = customers.filter((c) => Date.parse(c.joinedAt) < placed && c.status === 'ACTIVE');
    const cust = eligible.length ? eligible[Math.floor(Math.pow(r(), 1.8) * eligible.length)] : customers[0];
    const lines = Array.from({ length: 1 + Math.floor(r() * 3) }, () => {
      const p = pick(active);
      const variant = p.variants.length ? pick(p.variants).options : undefined;
      return { p, qty: 1 + (r() < 0.25 ? 1 : 0), variant };
    });
    const items = lines.map(({ p, qty, variant }) => ({ productId: p.id, slug: p.slug, name: p.name, price: p.price, quantity: qty, icon: p.icon, hue: p.hue, variant }));
    const subtotal = items.reduce((s, it) => s + it.price * it.quantity, 0);
    const useCoupon = subtotal >= 999 && r() < 0.22;
    const discount = useCoupon ? Math.min(300, Math.round(subtotal * 0.1)) : 0;
    const shipping = subtotal >= 999 ? 0 : 79;
    const total = subtotal - discount + shipping;
    const age = ageDays;
    const roll = r();
    let status: OrderStatus;
    if (age > 14) status = roll < 0.8 ? 'DELIVERED' : roll < 0.88 ? 'CANCELLED' : roll < 0.95 ? 'REFUNDED' : 'DELIVERED';
    else if (age > 6) status = roll < 0.55 ? 'DELIVERED' : roll < 0.85 ? 'SHIPPED' : roll < 0.93 ? 'PROCESSING' : 'CANCELLED';
    else if (age > 2) status = roll < 0.35 ? 'SHIPPED' : roll < 0.6 ? 'PROCESSING' : roll < 0.85 ? 'PAID' : roll < 0.95 ? 'PENDING' : 'CANCELLED';
    else status = roll < 0.45 ? 'PENDING' : roll < 0.85 ? 'PAID' : 'PROCESSING';
    const method: PaymentMethod = pick(['UPI', 'UPI', 'CARD', 'NETBANKING', 'COD']);
    const paidStatuses: OrderStatus[] = ['PAID', 'PROCESSING', 'SHIPPED', 'DELIVERED'];
    const paymentStatus: AdminOrder['paymentStatus'] =
      status === 'REFUNDED' ? 'REFUNDED' : status === 'PENDING' ? 'PENDING' : status === 'CANCELLED' ? (method === 'COD' ? 'FAILED' : 'REFUNDED')
        : method === 'COD' && status !== 'DELIVERED' ? 'PENDING' : 'PAID';
    const path: OrderStatus[] = ['PENDING'];
    if (status === 'CANCELLED') path.push('CANCELLED');
    else if (status !== 'PENDING') {
      path.push('PAID');
      if (['PROCESSING', 'SHIPPED', 'DELIVERED', 'REFUNDED'].includes(status) && status !== 'REFUNDED') path.push('PROCESSING');
      if (['SHIPPED', 'DELIVERED', 'REFUNDED'].includes(status)) { if (!path.includes('PROCESSING')) path.push('PROCESSING'); path.push('SHIPPED'); }
      if (['DELIVERED', 'REFUNDED'].includes(status)) path.push('DELIVERED');
      if (status === 'REFUNDED') path.push('REFUNDED');
    }
    const timeline = path.map((s, k) => ({ status: s, at: iso(placed + k * (k === 0 ? 0 : 0.6 * DAY) + (k ? 60_000 : 0)), note: k === 0 ? 'Order placed' : undefined }));
    const addr = addresses[cust.id][0];
    orders.push({
      id: idp('ord', i + 1), number: `${o.prefix}-${100200 + i}`, tenantId: T, status, placedAt: iso(placed), items,
      pricing: { subtotal, couponDiscount: discount, walletApplied: 0, pointsApplied: 0, shipping, taxIncluded: Math.round((total * 18) / 118), total, payable: total },
      paymentMethod: method, paymentStatus, address: addr, couponCode: useCoupon ? 'WELCOME10' : undefined, timeline,
      tracking: ['SHIPPED', 'DELIVERED', 'REFUNDED'].includes(status) ? { carrier: pick(['BlueDart', 'Delhivery', 'Ecom Express']), awb: `AWB${Math.floor(1e9 + r() * 8e9)}` } : undefined,
      customer: { id: cust.id, name: cust.name, email: cust.email, phone: cust.phone }, notes: [],
    });
    void paidStatuses;
  }
  orders.sort((a, b) => a.placedAt.localeCompare(b.placedAt));
  orders.forEach((od) => events.push({ t: Date.parse(od.placedAt), kind: 'order', idx: orders.indexOf(od) }));
  events.sort((a, b) => a.t - b.t);

  const cust = (id: string) => customers.find((c) => c.id === id)!;
  for (const ev of events) {
    if (ev.kind === 'topup') {
      addWallet(ev.c, 'TOP_UP', 'CREDIT', ev.amount, ev.t, { reference: 'Gateway top-up' });
      spendable[ev.c.id] = (spendable[ev.c.id] ?? 0) + ev.amount;
      continue;
    }
    const od = orders[ev.idx];
    const c = cust(od.customer.id);
    const bal = spendable[c.id] ?? 0;
    const success = !['CANCELLED', 'PENDING'].includes(od.status);
    if (bal >= 200 && r() < 0.5 && od.status !== 'PENDING') {
      const use = Math.min(bal, Math.round(od.pricing.total * (0.3 + 0.7 * r())));
      od.pricing.walletApplied = use;
      od.pricing.payable = od.pricing.total - use;
      if (od.pricing.payable === 0) od.paymentMethod = 'WALLET';
      spendable[c.id] = bal - use;
      addWallet(c, 'ORDER_PAYMENT', 'DEBIT', use, ev.t + 60_000, { reference: od.number });
      if (od.status === 'CANCELLED' || od.status === 'REFUNDED') {
        addWallet(c, 'REFUND', 'CREDIT', use, Date.parse(od.timeline[od.timeline.length - 1].at), { reference: od.number, reason: od.status === 'REFUNDED' ? 'Returned item' : 'Order cancelled' });
      }
    }
    if (success) addLoyalty(c, 'EARN', Math.floor(od.pricing.total / 100), `Purchase ${od.number}`, ev.t + 120_000);
    if (od.status === 'REFUNDED') addLoyalty(c, 'ADJUSTMENT', -Math.floor(od.pricing.total / 100), `Refund reversal ${od.number}`, Date.parse(od.timeline[od.timeline.length - 1].at));
  }
  // rewards + manual adjustments + a few redemptions
  customers.slice(0, 7).forEach((c, i) => {
    addWallet(c, 'REWARD', 'CREDIT', 50 + i * 25, SEED_NOW - (10 + i * 6) * DAY, { reason: 'Cashback campaign' });
    if (i < 6) addLoyalty(c, 'REDEEM', -30, 'Redeemed at checkout', SEED_NOW - (6 + i * 5) * DAY);
  });
  addWallet(customers[2], 'ADJUSTMENT', 'CREDIT', 150, SEED_NOW - 4 * DAY, { reason: 'Goodwill for delayed delivery', actorName: 'Meera Kapoor' });

  /* ---------- referrals ---------- */
  const spentBy = (id: string) => orders.filter((x) => x.customer.id === id && !['CANCELLED', 'REFUNDED'].includes(x.status)).reduce((s, x) => s + x.pricing.total, 0);
  const owners = [...customers].sort((a, b) => spentBy(b.id) - spentBy(a.id)).slice(0, 6);
  const referralLinks: ReferralLink[] = owners.map((c, i) => ({
    id: idp('ref', i + 1), tenantId: T, code: c.name.split(' ')[0].toUpperCase() + (20 + i), ownerId: c.id, ownerName: c.name,
    url: `https://${o.slug}.mce.store/?ref=${c.name.split(' ')[0].toUpperCase()}${20 + i}`,
    clicks: 60 + Math.floor(r() * 220), signups: 6 + Math.floor(r() * 18), conversions: 2 + Math.floor(r() * 9), active: i !== 5, createdAt: iso(SEED_NOW - (60 + i * 8) * DAY),
  }));
  const referred: ReferredCustomer[] = [];
  const commissions: Commission[] = [];
  const pool = customers.filter((c) => !owners.includes(c)).slice(0, 14);
  pool.forEach((c, i) => {
    const owner = owners[i % owners.length];
    const first = orders.find((x) => x.customer.id === c.id && ['PAID', 'PROCESSING', 'SHIPPED', 'DELIVERED'].includes(x.status));
    referred.push({ id: idp('rfd', i + 1), referrerId: owner.id, referrerName: owner.name, customerName: c.name, status: first ? 'PURCHASED' : 'SIGNED_UP', orderTotal: first?.pricing.total, date: c.joinedAt });
    if (first) {
      const status = (['PAID', 'APPROVED', 'PENDING', 'PENDING', 'REJECTED', 'PAID'] as const)[commissions.length % 6];
      const amount = Math.round(first.pricing.total * 0.05);
      const paidAt = status === 'PAID' ? iso(Date.parse(first.placedAt) + 8 * DAY) : undefined;
      commissions.push({ id: idp('com', commissions.length + 1), tenantId: T, referrerId: owner.id, referrerName: owner.name, orderNumber: first.number, orderTotal: first.pricing.total, amount, status, createdAt: first.placedAt, paidAt });
      if (status === 'PAID') addWallet(owner, 'REFERRAL_PAYOUT', 'CREDIT', amount, Date.parse(paidAt!), { reference: first.number, reason: 'Referral commission' });
    }
  });

  // final: order ledger chronologically, recompute snapshots
  wallet.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const run: Record<string, number> = {};
  wallet.forEach((w) => { run[w.customerId] = (run[w.customerId] ?? 0) + (w.direction === 'CREDIT' ? w.amount : -w.amount); w.balanceAfter = run[w.customerId]; });
  loyalty.sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  /* ---------- inventory ledger ---------- */
  const inventory: InventoryEntry[] = [];
  let inn = 1;
  const sold: Record<string, number> = {};
  orders.forEach((od) => { if (od.status !== 'CANCELLED') od.items.forEach((it) => (sold[it.productId] = (sold[it.productId] ?? 0) + it.quantity)); });
  products.forEach((p, i) => {
    const restock = i % 4 === 0 ? 40 : 0;
    const initial = p.stock + (sold[p.id] ?? 0) - restock;
    let cur = 0;
    const push = (delta: number, reason: InventoryEntry['reason'], at: number, note?: string, actor = 'System') => {
      inventory.push({ id: idp('inv', inn++), tenantId: T, productId: p.id, productName: p.name, sku: p.sku, delta, before: cur, after: cur + delta, reason, note, actorName: actor, createdAt: iso(at) });
      cur += delta;
    };
    push(Math.max(initial, 0), 'INITIAL', Date.parse(p.createdAt) + DAY, 'Opening stock');
    const mine = orders.filter((od) => od.status !== 'CANCELLED').flatMap((od) => od.items.filter((it) => it.productId === p.id).map((it) => ({ od, it })));
    mine.forEach(({ od, it }) => push(-it.quantity, 'SALE', Date.parse(od.placedAt), od.number));
    if (restock) push(restock, 'RESTOCK', SEED_NOW - 21 * DAY, 'Supplier delivery', 'Rohan Das');
  });
  inventory.sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  /* ---------- marketing ---------- */
  const coupons: AdminCoupon[] = [
    ...couponsSeed.map((c, i) => ({ ...c, id: idp('cpn', i + 1), tenantId: T, enabled: c.code !== 'FESTIVE25', firstOrderOnly: c.code === 'WELCOME10', createdAt: iso(SEED_NOW - (100 - i * 20) * DAY) })),
    { id: idp('cpn', 9), tenantId: T, code: 'VIP500', title: 'VIP ₹500 off', description: 'Flat ₹500 off for VIP customers on orders above ₹4,000.', type: 'FIXED' as const, value: 500, minOrder: 4000, expiresAt: '2027-01-31T23:59:00Z', usageLimit: 100, usedCount: 0, perCustomerLimit: 3, enabled: false, firstOrderOnly: false, createdAt: iso(SEED_NOW - 3 * DAY) },
  ];
  const banners: AdminBanner[] = bannerSeed.map((b, i) => ({ ...b, id: idp('ban', i + 1), tenantId: T, enabled: true, orientation: 'LANDSCAPE' as const, sortOrder: i }));
  banners.push({
    id: idp('ban', 9), tenantId: T, placement: 'PROMO', eyebrow: 'Coming soon', title: 'Festive Collection', subtitle: 'Early access for members.', cta: { label: 'Preview', to: '/offers' },
    hue: 15, icon: 'sparkles', enabled: true, startsAt: iso(SEED_NOW + 20 * DAY), endsAt: iso(SEED_NOW + 50 * DAY), orientation: 'LANDSCAPE', sortOrder: 9,
  });
  const offers: AdminOffer[] = [
    { id: idp('off', 1), tenantId: T, name: 'Weekend electronics sale', description: '15% off all electronics over ₹2,000.', type: 'PERCENT_OFF', value: 15, conditions: { minOrder: 2000, categoryId: categories[0]?.id, segment: 'ALL' }, startsAt: iso(SEED_NOW - 2 * DAY), endsAt: iso(SEED_NOW + 12 * DAY), enabled: true, priority: 1 },
    { id: idp('off', 2), tenantId: T, name: 'Free shipping for VIPs', description: 'Free shipping on every order for VIP customers.', type: 'FREE_SHIPPING', value: 0, conditions: { segment: 'VIP' }, startsAt: iso(SEED_NOW - 30 * DAY), endsAt: iso(SEED_NOW + 60 * DAY), enabled: true, priority: 2 },
    { id: idp('off', 3), tenantId: T, name: 'Welcome ₹100 off', description: 'Flat ₹100 off the first order over ₹799.', type: 'FLAT_OFF', value: 100, conditions: { minOrder: 799, segment: 'NEW' }, startsAt: iso(SEED_NOW - 60 * DAY), endsAt: iso(SEED_NOW - 5 * DAY), enabled: false, priority: 3 },
  ];
  const campaigns: AdminCampaign[] = [
    { id: idp('cmp', 1), tenantId: T, name: 'Welcome series', channel: 'EMAIL', audience: 'NEW', subject: 'Welcome! Here’s 10% off', message: 'Thanks for joining. Use WELCOME10 on your first order.', linkedCouponCode: 'WELCOME10', status: 'SENT', sentAt: iso(SEED_NOW - 12 * DAY), stats: { recipients: 214, opened: 121, clicked: 47, conversions: 19 }, createdAt: iso(SEED_NOW - 14 * DAY) },
    { id: idp('cmp', 2), tenantId: T, name: 'Win-back: we miss you', channel: 'EMAIL', audience: 'INACTIVE', subject: 'It’s been a while', message: 'Come back and see what’s new.', status: 'SCHEDULED', scheduledAt: iso(SEED_NOW + 2 * DAY), stats: { recipients: 0, opened: 0, clicked: 0, conversions: 0 }, createdAt: iso(SEED_NOW - 3 * DAY) },
    { id: idp('cmp', 3), tenantId: T, name: 'Wallet cashback nudge', channel: 'SMS', audience: 'WALLET_HOLDERS', subject: 'Use your wallet balance', message: 'You have wallet balance waiting. Spend it before it slips your mind!', status: 'DRAFT', stats: { recipients: 0, opened: 0, clicked: 0, conversions: 0 }, createdAt: iso(SEED_NOW - 1 * DAY) },
    { id: idp('cmp', 4), tenantId: T, name: 'VIP early access', channel: 'PUSH', audience: 'VIP', subject: 'Early access is live', message: 'Your VIP early access starts now.', status: 'CANCELLED', stats: { recipients: 0, opened: 0, clicked: 0, conversions: 0 }, createdAt: iso(SEED_NOW - 20 * DAY) },
  ];

  /* ---------- settings ---------- */
  const plan = planById(o.planId) ?? PLAN_DEFS[0];
  const featureOverrides: FeatureOverrides = o.featureOverrides ?? {};
  const features: FeatureFlags = computeEffectiveFeatures(plan.features, featureOverrides);
  const nav = (id: string, label: string, to: string, feature?: NavItem['feature']): NavItem => ({ id, label, to, enabled: true, feature });
  const store: StoreSettings = {
    name: o.name, legalName: `${o.name} Pvt. Ltd.`, tagline: demoTenant.tagline, email: demoTenant.contact.email, phone: demoTenant.contact.phone,
    address: { line1: '14, Residency Road', city: 'Bengaluru', state: 'Karnataka', pincode: '560025', country: 'India' },
    currency: 'INR', timezone: 'Asia/Kolkata',
    shipping: { freeThreshold: demoTenant.shipping.freeThreshold, flatFee: demoTenant.shipping.flatFee, handlingDays: 2 },
    tax: { rate: demoTenant.taxRate, inclusive: true, gstin: '29ABCDE1234F1Z5' },
    payments: [
      { id: 'razorpay', label: 'Razorpay (cards, UPI, netbanking)', enabled: false, status: 'NOT_CONNECTED' },
      { id: 'stripe', label: 'Stripe (international cards)', enabled: false, status: 'NOT_CONNECTED' },
      { id: 'upi', label: 'Direct UPI', enabled: true, status: 'TEST_MODE' },
      { id: 'cod', label: 'Cash on delivery', enabled: true, status: 'LIVE' },
    ],
    seo: { ...demoTenant.seo }, announcement: demoTenant.announcement ?? '',
  };
  const today = SEED_NOW;
  const traffic = Array.from({ length: 100 }, (_, i) => {
    const d = today - (99 - i) * DAY;
    const visits = Math.round(75 + r() * 55 + i * 0.25);
    return { date: iso(d).slice(0, 10), visits, carts: Math.round(visits * (0.11 + r() * 0.04)), checkouts: Math.round(visits * (0.05 + r() * 0.03)) };
  });

  return {
    tenantId: T, name: o.name, slug: o.slug, features, status: o.status ?? 'ACTIVE', createdAt: o.createdAt ?? iso(SEED_NOW - 200 * DAY),
    ownerName: o.ownerName ?? 'Store Owner', ownerEmail: o.ownerEmail, planId: plan.id, featureOverrides,
    products, categories, inventory, orders, customers, addresses, wallet, loyalty,
    coupons, banners, offers, campaigns,
    loyaltyConfig: {
      earn: { pointsPer100: demoTenant.loyalty.pointsPer100, signupBonus: 20, reviewBonus: 20, referralBonus: 50, minOrder: 0 },
      redeem: { pointValue: demoTenant.loyalty.pointValue, minRedeem: demoTenant.loyalty.minRedeem, maxRedeemPercent: 30 },
      expiryMonths: 12,
      tiers: [{ name: 'Bronze', minPoints: 0, multiplier: 1 }, { name: 'Silver', minPoints: 200, multiplier: 1.25 }, { name: 'Gold', minPoints: 500, multiplier: 1.5 }],
    },
    rewards: [
      { id: idp('rwd', 1), tenantId: T, title: '₹50 off your order', description: 'Redeem at checkout on any order.', cost: 50, active: true },
      { id: idp('rwd', 2), tenantId: T, title: '₹120 off your order', description: 'Applies to orders above ₹1,000.', cost: 100, active: true },
      { id: idp('rwd', 3), tenantId: T, title: 'Free express shipping', description: 'One-time express upgrade.', cost: 80, active: false },
    ],
    referralSettings: { commissionRate: 5, cookieDays: 30, minPayout: 100, payoutDestination: 'WALLET', autoApprove: false, referredDiscount: 10 },
    referralLinks, referred, commissions, store,
    branding: { primaryColor: demoTenant.theme.primaryColor, secondaryColor: demoTenant.theme.secondaryColor, accentColor: demoTenant.theme.accentColor, fontFamily: demoTenant.theme.fontFamily },
    theme: { borderRadius: demoTenant.theme.borderRadius, defaultMode: 'light', allowModeToggle: true },
    navigation: {
      header: [nav('h1', 'Home', '/'), nav('h2', 'Products', '/products', 'products'), nav('h3', 'Categories', '/categories', 'categories'), nav('h4', 'Offers', '/offers', 'coupons'), nav('h5', 'About', '/about'), nav('h6', 'Contact', '/contact')],
      footer: [
        { id: 'f1', title: 'Shop', items: [nav('f1a', 'All products', '/products', 'products'), nav('f1b', 'Categories', '/categories', 'categories'), nav('f1c', 'Offers', '/offers', 'coupons')] },
        { id: 'f2', title: 'Account', items: [nav('f2a', 'My account', '/account'), nav('f2b', 'Orders', '/account/orders', 'orders'), nav('f2c', 'Wallet', '/account/wallet', 'wallet')] },
      ],
    },
    system: {
      notifications: { newOrder: true, lowStock: true, newCustomer: false, payoutRequest: true, dailyDigest: false },
      orderPrefix: o.prefix,
      team: [
        { id: `${T}_tm_1`, name: o.ownerName ?? 'Meera Kapoor', email: o.ownerEmail, role: 'TENANT_ADMIN', lastActiveAt: iso(SEED_NOW - 3_600_000), status: 'ACTIVE' },
        { id: `${T}_tm_2`, name: 'Rohan Das', email: `staff@${o.ownerEmail.split('@')[1]}`, role: 'STAFF', lastActiveAt: iso(SEED_NOW - 26 * 3_600_000), status: 'ACTIVE' },
        { id: `${T}_tm_3`, name: 'Sana Ali', email: `sana@${o.ownerEmail.split('@')[1]}`, role: 'STAFF', lastActiveAt: iso(SEED_NOW - 9 * DAY), status: 'INVITED' },
      ],
    },
    traffic, idempotency: {}, notificationsRead: [],
  };
}

export const DEMO_TENANT_ID = demoTenant.id;
export const OTHER_TENANT_ID = 'tenant_northwind';
export const URBAN_THREADS_ID = 'tenant_urbanthreads';
export const GREENLEAF_ID = 'tenant_greenleaf';
export const TECHNEST_ID = 'tenant_technest';
export const FITFUEL_ID = 'tenant_fitfuel';
/** Every seeded tenant id, for platform-wide reads that need to enumerate tenants. */
export const ALL_TENANT_IDS = [DEMO_TENANT_ID, OTHER_TENANT_ID, URBAN_THREADS_ID, GREENLEAF_ID, TECHNEST_ID, FITFUEL_ID];

/**
 * Six tenants spanning every plan tier and status, so the Super Admin console (tenant list, dashboard
 * charts, plan distribution, subscriptions/payments) has something real to show. Demo Commerce keeps its
 * exact pre-Step-5 effective features (Enterprise plan, no overrides). Northwind keeps its Step 4 behaviour
 * (wallet off) but now via an explicit override on top of the Growth plan, demonstrating "override wins".
 */
export const buildSeed = (): Record<string, TenantData> => ({
  [DEMO_TENANT_ID]: seedTenant({
    tenantId: DEMO_TENANT_ID, name: demoTenant.name, slug: 'demo', seed: 20260919, productCount: 24, customerCount: 36, orderCount: 150,
    ownerName: 'Meera Kapoor', ownerEmail: 'admin@demo-commerce.store', prefix: 'MCE', planId: 'plan_enterprise', status: 'ACTIVE',
    createdAt: '2025-06-01T00:00:00Z',
  }),
  [OTHER_TENANT_ID]: seedTenant({
    tenantId: OTHER_TENANT_ID, name: 'Northwind Traders', slug: 'northwind', seed: 77, productCount: 5, customerCount: 6, orderCount: 12,
    ownerName: 'Northwind Owner', ownerEmail: 'owner@northwind.test', prefix: 'NW', planId: 'plan_growth', featureOverrides: { wallet: false },
    status: 'ACTIVE', createdAt: '2026-01-15T00:00:00Z',
  }),
  [URBAN_THREADS_ID]: seedTenant({
    tenantId: URBAN_THREADS_ID, name: 'Urban Threads', slug: 'urban-threads', seed: 1301, productCount: 6, customerCount: 5, orderCount: 14,
    ownerName: 'Ananya Rao', ownerEmail: 'owner@urbanthreads.test', prefix: 'UT', planId: 'plan_starter', status: 'ACTIVE', createdAt: '2026-03-10T00:00:00Z',
  }),
  [GREENLEAF_ID]: seedTenant({
    tenantId: GREENLEAF_ID, name: 'GreenLeaf Grocers', slug: 'greenleaf', seed: 4042, productCount: 5, customerCount: 4, orderCount: 8,
    ownerName: 'Karthik Iyer', ownerEmail: 'owner@greenleaf.test', prefix: 'GL', planId: 'plan_free', featureOverrides: { banners: true },
    status: 'TRIAL', createdAt: iso(SEED_NOW - 9 * DAY),
  }),
  [TECHNEST_ID]: seedTenant({
    tenantId: TECHNEST_ID, name: 'TechNest Electronics', slug: 'technest', seed: 5555, productCount: 8, customerCount: 6, orderCount: 20,
    ownerName: 'Priyanka Shah', ownerEmail: 'owner@technest.test', prefix: 'TN', planId: 'plan_growth', featureOverrides: { affiliate: true },
    status: 'ACTIVE', createdAt: '2025-11-20T00:00:00Z',
  }),
  [FITFUEL_ID]: seedTenant({
    tenantId: FITFUEL_ID, name: 'Fit Fuel Nutrition', slug: 'fitfuel', seed: 8871, productCount: 4, customerCount: 4, orderCount: 6,
    ownerName: 'Rajesh Kumar', ownerEmail: 'owner@fitfuel.test', prefix: 'FF', planId: 'plan_starter', status: 'SUSPENDED', createdAt: '2026-02-01T00:00:00Z',
  }),
});

export { slugify };
